import type { EngineKey } from '@components/wish-story/engines';

/*
 * The products behind the homepage's demos, each with its own page
 * (/product/<slug>) — June, 2026-09-23. One source for what each one is
 * made of: the demo cards' tables read their three rows from here.
 *
 * What's claimed is what the app computes, read from the backend
 * (growth_computations, commuting_computations, the day-plan contract):
 * WHO charts below 24 months and CDC's to 240, a percentile-tracking
 * adult-height projection checked against mid-parental height, routes
 * with traffic for every mode, per-mile cost, calories and CO₂, a
 * morning and evening trip proposed for each commute day, day plans
 * ranked on energy, travel time and opening hours, agendas written to
 * the calendar door to door. Adventures' three kinds are June's words.
 *
 * "Top LLMs" are described, not named: what a general-purpose model
 * gives out of the box, which is what a mom meets first.
 */

export type ProductSlug = 'adventures' | 'growth-curves' | 'commute-optimizer';

export type ProductKind = {
  /** The short name, as the demo tables show it. */
  title: string;
  body: string;
  points: readonly string[];
};

export type Product = {
  slug: ProductSlug;
  name: string;
  /** The wish, as a mom puts it. */
  wish: string;
  tagline: string;
  intro: string;
  /** The app answering it, in real time: its screen recording over its
   *  first frame, faded to white before the tab bar. The homepage plays
   *  the same at 2x. */
  screen: { video: string; poster: string; label: string };
  compare: {
    /** A top LLM's answer to the wish, out of the box. */
    llm: string;
    /** The Home CEO's. */
    ours: string;
    rows: readonly { topic: string; llm: string; ours: string }[];
  };
  kinds: Record<EngineKey, ProductKind>;
};

export const PRODUCTS: readonly Product[] = [
  {
    slug: 'adventures',
    name: 'Adventures',
    wish: 'How do I get the kids out of the house?',
    tagline: 'A whole Saturday, planned around your kids.',
    intro:
      'Tell The Home CEO the kind of day you’re after. It plans real places near you — timed, mapped and matched to your crew — and puts the day on your calendar.',
    screen: {
      video: '/videos/demo/adventures-1x.mp4',
      poster: '/videos/demo/adventures.jpg',
      label: 'The app planning a Saturday: stops with drive times and opening hours, one swapped for a stop nearer by.',
    },
    compare: {
      llm: 'Try a local park, the library, a children’s museum or a nature walk. Pack snacks, check the weather, and let the kids help choose!',
      ours: 'Your Saturday, planned: 9:30 a nature walk at Gamble Garden, 12:00 lunch on Castro Street, 9 minutes away, 2:00 the launch lab at the Ames NASA Visitor Center, 12 minutes on. Drive times in — and it’s on your calendar.',
      rows: [
        { topic: 'What’s near you this weekend', llm: 'General ideas that fit anywhere', ours: 'Real places and this week’s events, gathered ahead of time' },
        { topic: 'Right for your kids', llm: 'One list for every family', ours: 'Matched to the day’s energy and your kids’ ages and interests' },
        { topic: 'The logistics', llm: 'You check the hours and the drive', ours: 'Opening hours and drive times worked into the plan' },
        { topic: 'Follow-through', llm: 'You copy it into your calendar', ours: 'One tap puts the day on your calendar, door to door' },
      ],
    },
    kinds: {
      data: {
        title: 'Local Attractions and Events',
        body: 'Parks, trails, museums, playgrounds and the events on the day you pick are gathered ahead of time, so a plan comes back instant, local and current — not a guess.',
        points: [
          'Attractions near you, catalogued before you ask',
          'Events looked up for the date you choose',
          'Opening hours, indoor or outdoor, and memberships on file',
        ],
      },
      expert: {
        title: 'Energy, Inspiration and Transport Models',
        body: 'Every plan is shaped the way a parent who knows the area would shape it: the right pace for the day, something to spark curiosity, and stops that make sense in order.',
        points: [
          'A chill day or a big adventure, as you like',
          'Your kids’ ages and interests in the mix',
          'Stops ordered by travel time, arriving while they’re open',
        ],
      },
      skills: {
        title: 'Agendas on Calendar',
        body: 'The plan is ready to live: the day lands on your calendar door to door, with a time to be home by — easy to share, or to swap for a new plan.',
        points: ['The day on your calendar, door to door', 'Shared with whoever’s coming', 'A fresh plan in one tap'],
      },
    },
  },
  {
    slug: 'growth-curves',
    name: 'Growth Curves',
    wish: 'Are my kids growing okay?',
    tagline: 'Your family’s growth story, on the charts pediatricians use.',
    intro:
      'Add heights as you measure them. The Home CEO plots each child on WHO and CDC growth charts, projects adult height, and tells the story in one clean view you can share.',
    screen: {
      video: '/videos/demo/growth-curves-1x.mp4',
      poster: '/videos/demo/growth-curves.jpg',
      label: 'The app telling a child’s growth story: height today, predicted adult height, the family’s heights, and heights to aspire to.',
    },
    compare: {
      llm: 'Every child grows at their own pace. Growth charts can give you a sense of where your child falls — and if you’re worried, your pediatrician is the best person to ask.',
      ours: 'Oliver is in the 39th percentile for height and tracking steadily. Predicted adult height: 5′8″–5′10″. Time to measure again in three months.',
      rows: [
        { topic: 'The right chart', llm: 'Growth charts explained in general', ours: 'WHO charts to age two, CDC charts from two to twenty — plotted for your child' },
        { topic: 'Adult height', llm: 'A formula to work through yourself', ours: 'Projected from percentile tracking and both parents’ heights' },
        { topic: 'The whole family', llm: 'One question at a time', ours: 'Every child’s and parent’s percentile, side by side' },
        { topic: 'Staying on it', llm: 'Remembering is up to you', ours: 'A reminder when it’s time to measure again' },
      ],
    },
    kinds: {
      data: {
        title: 'WHO & CDC Charts',
        body: 'The growth references pediatricians use are built in — WHO for babies and toddlers, CDC from two to twenty — with BMI-for-age alongside height. And, for fun, the famous people who share your child’s predicted height.',
        points: [
          'WHO growth charts to 24 months',
          'CDC growth charts from 2 to 20 years',
          'Heightspiration: who shares their predicted height',
        ],
      },
      expert: {
        title: 'Percentile Tracking and Mid-Parental Height',
        body: 'Adult height is projected the way specialists do it: from how a child tracks along their percentile over time, checked against a target set by both parents’ heights.',
        points: ['Percentile tracking over time', 'A mid-parental target height', 'Family percentiles, side by side'],
      },
      skills: {
        title: 'Growth Stories, Sharing and Re-measure Reminders',
        body: 'Measurements become a story you can read at a glance and send to family — with a nudge when it’s time to measure again.',
        points: ['A growth story for each child', 'Shared in one tap', 'A reminder when a new measurement is due'],
      },
    },
  },
  {
    slug: 'commute-optimizer',
    name: 'Commute Optimizer',
    wish: 'New job… what’s the commute going to be?',
    tagline: 'Every way to work, compared in miles, minutes and dollars.',
    intro:
      'Give The Home CEO two addresses. It compares driving, transit, biking and walking at the hours you’d actually go — cost, time and health — then plans your commute week.',
    screen: {
      video: '/videos/demo/commute-1x.mp4',
      poster: '/videos/demo/commute.jpg',
      label: 'The app comparing a commute on foot, by bike, by transit and by car — time, cost, steps and calories — then all of them on one chart.',
    },
    compare: {
      llm: 'Driving is usually fastest, transit can save money, and biking is great exercise. Check a maps app for traffic at the times you’d leave.',
      ours: 'Biking is best overall for your commute — the lowest cost and the most exercise. Driving is fastest, and the priciest once parking is counted. Your week is planned: a morning and an evening trip on each commute day.',
      rows: [
        { topic: 'Traffic when you’d go', llm: 'Typical travel times', ours: 'Traffic at the hours you’d leave, across the week' },
        { topic: 'The true cost', llm: 'Gas, roughly', ours: 'Fuel, fares, parking, upkeep, insurance and depreciation' },
        { topic: 'Health and footprint', llm: 'General pros and cons', ours: 'Calories, steps and CO₂ for every way to go' },
        { topic: 'Your week', llm: 'Left to you', ours: 'A morning and an evening trip on every commute day' },
      ],
    },
    kinds: {
      data: {
        title: 'Live Traffic, Transit Fares and Fuel Prices',
        body: 'Routes for driving, transit, biking and walking are looked up with traffic, alongside your state’s fuel and electricity prices and your area’s transit fares.',
        points: [
          'Routes with traffic for every way to go',
          'State fuel and electricity prices',
          'Local transit fares and registration fees',
        ],
      },
      expert: {
        title: 'Per-Mile Cost, Calorie and CO₂ Models',
        body: 'Each way to go is priced the way a transportation analyst would price it — fuel economy, maintenance, depreciation and insurance per mile — with the calories it burns and the carbon it costs, then scored from Dreamy to Brutal.',
        points: [
          'The true cost per mile, not just gas',
          'Calories and steps for active commutes',
          'CO₂ for every mode',
          'A score from Dreamy to Brutal',
        ],
      },
      skills: {
        title: 'Ranked Ways to Go and a Commute Week Scheduled',
        body: 'The options come back ranked for your life, with parking and days per week dialed in, and the assistant proposes a morning and an evening trip for every day you commute.',
        points: [
          'Every way to go, ranked on cost, time and health',
          'Parking and days per week dialed in',
          'A morning and evening trip on each commute day',
        ],
      },
    },
  },
];

export const productBySlug = (slug: string | undefined): Product | undefined =>
  PRODUCTS.find((p) => p.slug === slug);

/** A product's three kinds by their short names — a demo card's table. */
export const kindTitles = (slug: ProductSlug): Record<EngineKey, string> => {
  const product = productBySlug(slug);
  if (!product) throw new Error(`No product ${slug}`);
  return { data: product.kinds.data.title, expert: product.kinds.expert.title, skills: product.kinds.skills.title };
};

/** A product's page. */
export const productPath = (slug: ProductSlug) => `/product/${slug}`;
